import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UndefinedInitialQueryOptions,
  type UseQueryReturnType
} from '@tanstack/vue-query';
import { EDisabledFeatures } from '@/types/composables/useDisabledFeatureEnums';
import axiosInstance from '@/plugins/axios';
import useDisabledFeature from '@/composables/useDisabledFeature';
import type {
  ISettingsDeleteFilterRequest,
  ISettingsFilterItem,
  ISettingsFilterItemRequest,
  ISettingsFilterMoveToBottom,
  ISettingsImportFilterRequest
} from '@/types/services/filter.service';
import type { AxiosResponse } from 'axios';
import type { DefaultError } from '@tanstack/query-core';
import useSnackbar from '@/composables/useSnackbar';
import { useI18n } from 'vue-i18n';
import { isBlockedSenderFilter } from '@/helpers/filter';

const useFilterService = () => {
  const { t } = useI18n();
  const { addSnackbar } = useSnackbar();
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();
  const { isFeatureDisabled } = useDisabledFeature();
  const queryClient = useQueryClient();

  const listFiltersQueryKey = ['authenticatedQuery', userIndex, 'filters'];

  const listFiltersQueryOptions = (): UndefinedInitialQueryOptions<ISettingsFilterItem[] | null> =>
    queryOptions<ISettingsFilterItem[] | null>({
      queryKey: listFiltersQueryKey,
      queryFn: async ({ signal }) => {
        if (await isFeatureDisabled(EDisabledFeatures.FILTERS)) return null;
        const res = await axiosInstance.get(`/u/${userIndex.value}/filters`, { signal });
        return res.data;
      },
      enabled: () => queriesEnabled.value
    });

  const listFilters = (): UseQueryReturnType<ISettingsFilterItem[] | null, DefaultError> => {
    return useQuery({
      ...listFiltersQueryOptions(),
      select: (data) => (data ? data.filter((f) => !isBlockedSenderFilter(f)) : null)
    });
  };

  const listBlockedSenderFilters = (): UseQueryReturnType<
    ISettingsFilterItem[] | null,
    DefaultError
  > => {
    return useQuery({
      ...listFiltersQueryOptions(),
      select: (data) => (data ? data.filter((f) => isBlockedSenderFilter(f)) : null)
    });
  };

  const listAllFilters = (): UseQueryReturnType<ISettingsFilterItem[] | null, DefaultError> => {
    return useQuery(listFiltersQueryOptions());
  };

  const createFilter = () => {
    return useMutation<void, DefaultError, Omit<ISettingsFilterItemRequest, 'id'>>({
      mutationKey: ['authenticatedQuery', userIndex, 'filters', 'create'],
      mutationFn: async (data) => {
        await axiosInstance.post(`/u/${userIndex.value}/filters`, data);
      },
      onSuccess: () => {
        addSnackbar({ type: 'success', message: t('settings.filters.item.success.saved') });
      }
    });
  };

  const updateFilter = () => {
    return useMutation<void, DefaultError, ISettingsFilterItemRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'filters', 'update'],
      mutationFn: async (data) => {
        await axiosInstance.patch(`/u/${userIndex.value}/filters/${data.id}`, data);
      },
      onSuccess: () => {
        addSnackbar({ type: 'success', message: t('settings.filters.item.success.updated') });
      }
    });
  };

  const updateFilterDisabledStatus = () => {
    return useMutation<
      void,
      DefaultError,
      Pick<ISettingsFilterItem, 'id' | 'disabled'>,
      { previousResponse: ISettingsFilterItem[] | null | undefined }
    >({
      mutationKey: ['authenticatedQuery', userIndex, 'filters', 'disable'],
      mutationFn: async (data) => {
        await axiosInstance.patch(`/u/${userIndex.value}/filters/${data.id}`, {
          disabled: data.disabled
        });
      },
      onMutate: async (newUpdateRequestData) => {
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({ queryKey: listFiltersQueryKey });

        // Snapshot the previous value
        const previousResponse = queryClient.getQueryData<ISettingsFilterItem[] | null>(
          listFiltersQueryKey
        );

        // Optimistically update to the new value
        queryClient.setQueryData<ISettingsFilterItem[] | null>(
          listFiltersQueryKey,
          (oldFilters): ISettingsFilterItem[] | null | undefined => {
            if (!oldFilters) {
              return undefined;
            }

            return oldFilters.map((filter) => ({
              ...filter,
              disabled:
                filter.id === newUpdateRequestData.id
                  ? newUpdateRequestData.disabled
                  : filter.disabled
            }));
          }
        );

        // Return a context object with the snapshotted value
        return { previousResponse };
      },
      // If the mutation fails,
      // use the context returned from onMutate to roll back
      onError: (_, __, context) => {
        if (context?.previousResponse) {
          queryClient.setQueryData<ISettingsFilterItem[] | null>(
            listFiltersQueryKey,
            context.previousResponse
          );
        } else {
          queryClient.invalidateQueries({ queryKey: listFiltersQueryKey });
        }
      }
    });
  };

  const updateAllFiltersDisabledStatus = () => {
    return useMutation<
      void,
      DefaultError,
      boolean,
      { previousResponse: ISettingsFilterItem[] | null | undefined }
    >({
      mutationKey: ['authenticatedQuery', userIndex, 'filters', 'disable'],
      mutationFn: async (data) => {
        await axiosInstance.post(`/u/${userIndex.value}/filters/massToggle`, { disabled: data });
      },
      onMutate: async (newUpdateRequestData) => {
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({ queryKey: listFiltersQueryKey });

        // Snapshot the previous value
        const previousResponse = queryClient.getQueryData<ISettingsFilterItem[] | null>(
          listFiltersQueryKey
        );

        // Optimistically update to the new value
        queryClient.setQueryData<ISettingsFilterItem[] | null>(
          listFiltersQueryKey,
          (oldFilters): ISettingsFilterItem[] | null | undefined => {
            if (!oldFilters) {
              return undefined;
            }

            return oldFilters.map((filter) => ({ ...filter, disabled: newUpdateRequestData }));
          }
        );

        // Return a context object with the snapshotted value
        return { previousResponse };
      },
      // If the mutation fails,
      // use the context returned from onMutate to roll back
      onError: (_, __, context) => {
        if (context?.previousResponse) {
          queryClient.setQueryData<ISettingsFilterItem[] | null>(
            listFiltersQueryKey,
            context.previousResponse
          );
        } else {
          queryClient.invalidateQueries({ queryKey: listFiltersQueryKey });
        }
      }
    });
  };

  const moveToBottom = () => {
    return useMutation<void, DefaultError, ISettingsFilterMoveToBottom>({
      mutationKey: ['authenticatedQuery', userIndex, 'filters', 'moveToBottom'],
      mutationFn: async ({ id }) => {
        await axiosInstance.post(`/u/${userIndex.value}/filters/moveToBottom/${id}`);
      },
      onSuccess: () => {
        addSnackbar({ type: 'success', message: t('settings.filters.success.movedToBottom') });
      }
    });
  };

  const deleteFilters = () => {
    return useMutation<void, DefaultError, ISettingsDeleteFilterRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'filters', 'massDelete'],
      mutationFn: async (data) => {
        await axiosInstance.post(`/u/${userIndex.value}/filters/massDelete`, data);
      }
    });
  };

  const exportFilters = () => {
    return useMutation<AxiosResponse<Blob>, DefaultError>({
      mutationKey: ['authenticatedQuery', userIndex, 'filters', 'export', 'xml'],
      mutationFn: async () => {
        return await axiosInstance.get(`/u/${userIndex.value}/filters/export`, {
          responseType: 'blob',
          timeout: 0
        });
      },
      onSuccess: () => {
        addSnackbar({ message: t('settings.filters.exportDialog.completed'), type: 'success' });
      }
    });
  };

  const importFilters = () => {
    return useMutation<void, DefaultError, ISettingsImportFilterRequest>({
      mutationKey: ['authenticatedQuery', userIndex, 'filters', 'import', 'xml'],
      mutationFn: async (data) => {
        const filters = new FormData();
        filters.append('filtersImport', data.filtersImport);

        await axiosInstance.post(`/u/${userIndex.value}/filters/import`, filters);
      },
      onSuccess: () => {
        addSnackbar({ message: t('settings.filters.importDialog.uploaded'), type: 'success' });
      }
      // onError: () => {
      //   addSnackbar({
      //     message: t('settings.filters.importDialog.failed'),
      //     type: 'error'
      //   });
      // }
    });
  };

  return {
    listFilters,
    listAllFilters,
    listBlockedSenderFilters,
    createFilter,
    updateFilter,
    moveToBottom,
    deleteFilters,
    importFilters,
    exportFilters,
    updateFilterDisabledStatus,
    updateAllFiltersDisabledStatus
  };
};

export default useFilterService;
